import { useContext, useEffect, useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Loader,
  NotificationType,
  Tab,
  TabHeader,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";
import {
  DateRangePreset,
  DateRangeSelector,
  MetricPickerModal,
  OutcomeTiles,
  OutcomesEmptyState,
  OutcomesEmptyStateKind,
  defaultPickerDraft,
  metricById,
  resolveDateFilter,
  resolveDateRange,
  useMetricRegistry,
} from "@transfr-inc/dashboard-components/outcome-metrics";
import { Product } from "@transfr-inc/dashboard-components/constants";

import { MULTI_ORG_PARENT } from "@constants/organization-types";
import { SessionContext } from "@context/session.context";
import { useNotification } from "@hooks/use-notification";
import { ApplicationId } from "@lib/constants";
import {
  ConfigurationKeys,
  ConfigurationRequestKeys,
} from "@services/configuration";

import { DeckHistoryRow } from "./impact-report.deck-history";
import { ImpactReportPromptConfigModal } from "./impact-report.prompt-config.modal";
import { ImpactReportMetricRegistry } from "./impact-report.metric-registry";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";
import { parseStringBoolean } from "@utils/utils";

import "./impact-report.tab.scss";

// v1 ships a single report type; the selector scopes the form for when more
// report types exist.
const REPORT_TYPES = [{ id: "standard", name: "Impact Report" }];
const DEFAULT_REPORT_TYPE = REPORT_TYPES[0];

// The saved inputs pack carries exactly this many headline metrics: the top
// of the picker's ordered selection become the headline outcome tiles.
const HEADLINE_METRIC_COUNT = 4;

// The internal portal has no per-org licensing wired into this tab yet, and
// the metric registry's role gating has no CS roles — stub the full catalog
// (org-admin view, every product line) until real values flow in.
const ALL_PRODUCTS = [
  Product.TS,
  Product.CE,
  Product.TRK,
  Product.PROG,
  Product.BASICS,
];

const GOAL_PLACEHOLDERS = [
  "e.g. Expand career exposure — give every 9th–10th grader at least 3 hands-on career simulations before course selection.",
  "e.g. Raise CTE completion — lift program completion rate 10 points year-over-year across enrolled learners.",
  "e.g. Grow credential attainment — 200 learners earn an industry-recognized credential this academic year.",
];

/**
 * One metric's tile state read off the summary request: in flight reads as
 * loading, a failed request as an error, and a metric the response omits as
 * "no data" — never a 0 the backend didn't send. Mirrors unified-dashboard's
 * consumption contract for the outcomes proxy.
 */
function tileFromSummary({ loading, error, response }, metricId) {
  if (loading) {
    return { isLoading: true };
  }
  if (error) {
    return { isError: true };
  }
  const entry = response?.metrics?.[metricId];
  return entry ? { value: entry.value } : { isEmpty: true };
}

/** The concrete { start, end } range the current date filter covers. */
function resolveRange(dateFilter) {
  if (dateFilter?.start && dateFilter?.end) {
    return { start: dateFilter.start, end: dateFilter.end };
  }
  return resolveDateRange(dateFilter?.preset);
}

export function ImpactReportBuilder({ organization, childOrgs }) {
  const registry = useMetricRegistry();
  const { impactReportService, configurationService } = services;
  const { addNotification } = useNotification();
  const { role } = useContext(SessionContext);
  const orgCode = organization.code;

  // Dev tooling gate: the prompt-config button only renders when the global
  // config-service flag is on. Read from the app-wide params (org-independent).
  const [promptConfigEnabled, setPromptConfigEnabled] = useState(false);
  const [promptConfigOpen, setPromptConfigOpen] = useState(false);
  useEffect(() => {
    configurationService
      .getConfigurations(ApplicationId.DASHBOARD, role)
      .then((response) => {
        const settings =
          response?.[ConfigurationRequestKeys.CONFIG_SETTINGS] ?? response;
        const param = settings?.find(
          (config) =>
            config.name ===
            ConfigurationKeys.IMPACT_REPORT_PROMPT_CONFIG_ENABLED,
        );
        setPromptConfigEnabled(parseStringBoolean(param?.value));
      })
      .catch((error) => {
        console.error(
          "[Impact Report] - Failed to load prompt config flag",
          error,
        );
        setPromptConfigEnabled(false);
      });
  }, []);

  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);
  // The filter always carries concrete start/end dates (resolved locally from
  // a chosen preset) so the selector's trigger can show the covered range —
  // there is no server round-trip resolving presets on this tab.
  const [dateFilter, setDateFilter] = useState(() =>
    resolveDateFilter({ preset: DateRangePreset.ACADEMIC_YEAR }),
  );
  // Exactly HEADLINE_METRIC_COUNT checked from the start, so the capped picker
  // never opens over its limit.
  const [draft, setDraft] = useState(() => {
    const base = defaultPickerDraft(registry, {
      licensedProducts: ALL_PRODUCTS,
    });
    return {
      ...base,
      checkedIds: base.checkedIds.slice(0, HEADLINE_METRIC_COUNT),
    };
  });
  const [goals, setGoals] = useState(["", "", ""]);
  const [lookingAhead, setLookingAhead] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  const { loading, response } = useApiRequest(() =>
    impactReportService.getInputs(orgCode, reportType.id),
  );

  const { loading: saving, sendRequest: sendSaveRequest } = useApiRequest(
    (pack) => impactReportService.saveInputs(orgCode, reportType.id, pack),
    false,
  );

  // Local state rather than useApiRequest: the notification needs the API
  // error detail at call time, which the hook swallows into render state.
  const [generating, setGenerating] = useState(false);

  const { response: decksResponse, sendRequest: sendDecksRequest } =
    useApiRequest(() => impactReportService.getDecks(orgCode));
  const decks = decksResponse?.decks ?? [];

  // Seed the form from the saved pack; a GET with `inputs: null` (nothing
  // saved yet) leaves the defaults in place.
  useEffect(() => {
    const inputs = response?.inputs;
    if (!inputs) {
      return;
    }
    setDateFilter(inputs.dateRange);
    setDraft((current) => ({
      checkedIds: inputs.metrics,
      // Keep the full catalog order, with the saved headline metrics first.
      orderedIds: [
        ...inputs.metrics,
        ...current.orderedIds.filter((id) => !inputs.metrics.includes(id)),
      ],
    }));
    setGoals([
      inputs.goals?.[0] ?? "",
      inputs.goals?.[1] ?? "",
      inputs.goals?.[2] ?? "",
    ]);
    setLookingAhead(inputs.lookingAhead ?? "");
  }, [response]);

  // The headline metrics: the picker's ordered selection, capped at the count
  // the report (and the saved pack) carries.
  const headlineIds = useMemo(() => {
    const checked = new Set(draft.checkedIds);
    return draft.orderedIds
      .filter((id) => checked.has(id))
      .slice(0, HEADLINE_METRIC_COUNT);
  }, [draft]);

  const headlineMetrics = useMemo(
    () => headlineIds.map((id) => metricById(registry, id)).filter(Boolean),
    [registry, headlineIds],
  );

  const { start: rangeStart, end: rangeEnd } = resolveRange(dateFilter);

  // The summary API takes a flat list of org codes — no server-side hierarchy
  // expansion. A multi-org parent is an umbrella: its usage lives in the child
  // orgs, so its scope is the child codes alone and its own code is never
  // sent (unified-dashboard's multi-org convention). A parent with no
  // children has an empty scope — no request, tiles show "no data".
  const isParentOrg = organization.typeId === MULTI_ORG_PARENT.id;
  const summaryOrgCodes = useMemo(
    () =>
      isParentOrg ? (childOrgs ?? []).map((child) => child.code) : [orgCode],
    [isParentOrg, orgCode, childOrgs],
  );

  const {
    loading: summaryLoading,
    response: summaryResponse,
    error: summaryError,
    sendRequest: sendSummaryRequest,
  } = useApiRequest(
    () =>
      impactReportService.getDashboardSummary(summaryOrgCodes, {
        startDate: rangeStart,
        endDate: rangeEnd,
        metrics: headlineIds,
      }),
    false,
  );

  // Live values re-pull whenever the org, period, or headline selection
  // changes; only the tile data refreshes — selection and goals are kept.
  const headlineIdsKey = headlineIds.join(",");
  const summaryOrgCodesKey = summaryOrgCodes.join(",");
  useEffect(() => {
    if (summaryOrgCodes.length === 0) {
      return;
    }
    sendSummaryRequest();
  }, [summaryOrgCodesKey, rangeStart, rangeEnd, headlineIdsKey]);

  // A 403 means the viewer lacks outcomes access to this org: the whole tile
  // row gives way to the access-denied state (see the render below). Any other
  // failure surfaces per tile.
  const accessDenied = summaryError?.status === 403;

  const tileData = useMemo(
    () =>
      Object.fromEntries(
        headlineIds.map((id) => [
          id,
          tileFromSummary(
            {
              loading: summaryLoading,
              error: summaryError,
              response: summaryResponse,
            },
            id,
          ),
        ]),
      ),
    [headlineIds, summaryLoading, summaryError, summaryResponse],
  );

  const canSave = headlineIds.length === HEADLINE_METRIC_COUNT;

  const buildPack = () => ({
    dateRange: resolveRange(dateFilter),
    metrics: headlineIds,
    goals: goals.map((goal) => goal.trim()).filter(Boolean),
    lookingAhead: lookingAhead.trim() || undefined,
  });

  const onSaveInputs = async () => {
    const saved = await sendSaveRequest(buildPack());
    addNotification({
      type: saved ? NotificationType.success : NotificationType.error,
      message: saved
        ? "Report inputs saved."
        : "We couldn't save the report inputs. Please try again.",
      ttl: 5000,
    });
  };

  // Generation saves the current inputs server-side first (same upsert as
  // Save), so the deck always matches the form. Expect ~15-60s.
  const onGenerate = async () => {
    setGenerating(true);
    try {
      const deck = await impactReportService.generate(
        orgCode,
        reportType.id,
        buildPack(),
      );
      addNotification({
        type: NotificationType.success,
        message: "Impact report generated.",
        ttl: 5000,
      });
      window.open(deck.url, "_blank", "noopener");
    } catch (error) {
      console.error("[Impact Report] - Failed to generate report", error);
      addNotification({
        type: NotificationType.error,
        message:
          error?.data?.detail ??
          "We couldn't generate the report. Please try again.",
        ttl: 8000,
      });
    } finally {
      setGenerating(false);
      // Success prepends the new deck; a failed run also lands in history.
      sendDecksRequest();
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="impact-report-builder">
        <div className="impact-report-builder-content">
          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">1</span>
              <h3>Report type &amp; reporting period</h3>
            </div>
            <div className="impact-report-builder-period">
              <Dropdown
                className="impact-report-builder-report-type"
                selected={reportType}
                onOptionSelected={(option) => setReportType(option)}
              >
                <DropdownButton placeholder="Select report type...">
                  {reportType.name}
                </DropdownButton>
                <DropdownItems>
                  {REPORT_TYPES.map((type) => (
                    <DropdownItem key={type.id} value={type}>
                      {type.name}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
              <DateRangeSelector
                dateFilter={dateFilter}
                onApply={(nextFilter, { onSuccess }) => {
                  setDateFilter(resolveDateFilter(nextFilter));
                  onSuccess();
                }}
              />
            </div>
            <p className="impact-report-builder-hint">
              Changing the period re-pulls outcome data; your inputs are kept.
            </p>
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">2</span>
              <h3>Outcome data &amp; metric selection</h3>
              <Button
                className="impact-report-builder-configure"
                onClick={() => setPickerOpen(true)}
              >
                Configure metrics
              </Button>
            </div>
            <p className="impact-report-builder-hint">
              Pick which metrics lead the report and rank them — the top{" "}
              {HEADLINE_METRIC_COUNT} become the headline outcome tiles. Values
              are pulled and QA&apos;d by the tool; you never edit the numbers.
            </p>
            {accessDenied ? (
              <OutcomesEmptyState
                kind={OutcomesEmptyStateKind.ACCESS_DENIED}
                level="breakdown"
              />
            ) : (
              <OutcomeTiles metrics={headlineMetrics} tileData={tileData} />
            )}
            {pickerOpen && (
              <MetricPickerModal
                seed={draft}
                licensedProducts={ALL_PRODUCTS}
                selectionLimit={HEADLINE_METRIC_COUNT}
                isSaving={false}
                onClose={() => setPickerOpen(false)}
                onSave={({ selectedMetricIds, metricOrder }) => {
                  setDraft({
                    checkedIds: selectedMetricIds,
                    orderedIds: metricOrder,
                  });
                  setPickerOpen(false);
                }}
              />
            )}
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">3</span>
              <h3>Add goals</h3>
              <span className="impact-report-builder-optional">Optional</span>
            </div>
            <p className="impact-report-builder-hint">
              Describe each program goal in your own words. These appear on the
              account dashboard beside the outcome data — add any that help, or
              skip this step entirely.
            </p>
            {goals.map((goal, index) => (
              <Textarea
                key={`goal-${index}`}
                label={`Goal ${index + 1} language`}
                className="impact-report-deck-goal-textarea"
                placeholder={GOAL_PLACEHOLDERS[index]}
                value={goal}
                onChange={({ target }) =>
                  setGoals((current) =>
                    current.map((g, i) => (i === index ? target.value : g)),
                  )
                }
              />
            ))}
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">4</span>
              <h3>Looking ahead</h3>
              <span className="impact-report-builder-optional">Optional</span>
            </div>
            <p className="impact-report-builder-hint">
              Where is this partnership going next? Rough bullets are fine —
              they&apos;re polished into the report&apos;s closing section.
            </p>
            <Textarea
              label="Looking ahead notes"
              className="impact-report-deck-goal-textarea"
              placeholder="e.g. Expand into the CNA pathway — insert Transfr as a program prerequisite; grow CTE alignment; continue career exploration integration."
              value={lookingAhead}
              onChange={({ target }) => setLookingAhead(target.value)}
            />
          </section>
        </div>
        <div className="impact-report-builder-card">
          {promptConfigEnabled && (
            <>
              <button
                type="button"
                className="impact-report-prompt-config-button"
                title="Prompt config (dev)"
                aria-label="Prompt config (dev)"
                onClick={() => setPromptConfigOpen(true)}
              >
                <FontAwesomeIcon icon="fa-regular fa-sliders" />
              </button>
              <ImpactReportPromptConfigModal
                open={promptConfigOpen}
                onClose={() => setPromptConfigOpen(false)}
                reportType={reportType.id}
              />
            </>
          )}
          {decks.length > 0 ? (
            <>
              <h2>
                Past decks
                <span className="impact-report-deck-count">{decks.length}</span>
              </h2>
              <ul className="impact-report-deck-list">
                {decks.map((deck) => (
                  <DeckHistoryRow
                    key={deck.id}
                    deck={deck}
                    typeName={
                      REPORT_TYPES.find((type) => type.id === deck.reportType)
                        ?.name ?? deck.reportType
                    }
                  />
                ))}
              </ul>
            </>
          ) : (
            <>
              <h2>Impact Report Builder</h2>
              <p>
                No report yet · save inputs and generate a 2-page core draft
              </p>
            </>
          )}
        </div>
      </div>
      <div className="impact-report-builder-footer">
        {!canSave && (
          <span className="impact-report-builder-hint">
            Select exactly {HEADLINE_METRIC_COUNT} metrics to save.
          </span>
        )}
        <Button
          disabled={!canSave || saving || generating}
          loader={saving}
          onClick={onSaveInputs}
        >
          Save inputs
        </Button>
        <Button
          className="impact-report-builder-generate"
          primary
          disabled={!canSave || saving || generating}
          loader={generating}
          onClick={onGenerate}
        >
          Generate Impact Report
        </Button>
      </div>
    </>
  );
}

export function ImpactReportTab({ organization, childOrgs }) {
  return {
    name: "reportBuilder",
    header: (
      <TabHeader name="reportBuilder" className="impact-report-tab-header">
        <div className="tab-header-text">Report Builder</div>
        <span className="impact-report-beta-badge">BETA</span>
      </TabHeader>
    ),
    content: (
      <Tab name="reportBuilder" className="impact-report-tab-panel">
        {organization?.code && (
          <ImpactReportMetricRegistry>
            <ImpactReportBuilder
              organization={organization}
              childOrgs={childOrgs}
            />
          </ImpactReportMetricRegistry>
        )}
      </Tab>
    ),
  };
}
